import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Alert,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ErrorOutline as ErrorOutlineIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { Popconfirm } from "antd";
import moment from "moment";
import toast from "react-hot-toast";

const ModalNotasDebitos = ({
  open,
  handleClose,
  clienteId,
  nomeCliente,
  sendRequest,
  auth,
  onNotasUpdate,
  notasDebitos,
}) => {
  const [novaNotaTexto, setNovaNotaTexto] = useState("");
  const [novaNotaTipo, setNovaNotaTipo] = useState("normal");
  const [notaEditando, setNotaEditando] = useState(null);
  const [textoEditando, setTextoEditando] = useState("");
  const [tipoEditando, setTipoEditando] = useState("normal");
  const [loading, setLoading] = useState(false);

  const adicionarNota = async () => {
    if (!novaNotaTexto.trim()) {
      toast.error("O texto da nota é obrigatório");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("texto", novaNotaTexto.trim());
      formData.append("tipo", novaNotaTipo);
      formData.append("utilizador", auth.userId);

      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/clientes/notadebito/${clienteId}`,
        "POST",
        formData,
        { Authorization: "Bearer " + auth.token }
      );

      onNotasUpdate(response.cliente.notasDebitos || []);
      setNovaNotaTexto("");
      setNovaNotaTipo("normal");
      toast.success("Nota adicionada com sucesso");
    } catch (err) {
      console.error("Erro ao adicionar nota:", err);
      toast.error("Erro ao adicionar nota");
    } finally {
      setLoading(false);
    }
  };

  const iniciarEdicao = (nota) => {
    setNotaEditando(nota._id);
    setTextoEditando(nota.texto);
    setTipoEditando(nota.tipo);
  };

  const cancelarEdicao = () => {
    setNotaEditando(null);
    setTextoEditando("");
    setTipoEditando("normal");
  };

  const salvarEdicao = async () => {
    if (!textoEditando.trim()) {
      toast.error("O texto da nota é obrigatório");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("texto", textoEditando.trim());
      formData.append("tipo", tipoEditando);
      formData.append("notaId", notaEditando);
      formData.append("utilizador", auth.userId);

      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/clientes/notadebito/${clienteId}`,
        "PATCH",
        formData,
        { Authorization: "Bearer " + auth.token }
      );

      onNotasUpdate(response.cliente.notasDebitos || []);
      cancelarEdicao();
      toast.success("Nota editada com sucesso");
    } catch (err) {
      console.error("Erro ao editar nota:", err);
      toast.error("Erro ao editar nota");
    } finally {
      setLoading(false);
    }
  };

  const removerNota = async (notaId) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("notaId", notaId);
      formData.append("utilizador", auth.userId);

      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/clientes/notadebito/${clienteId}`,
        "DELETE",
        formData,
        { Authorization: "Bearer " + auth.token }
      );

      onNotasUpdate(response.cliente.notasDebitos || []);
      toast.success("Nota removida com sucesso");
    } catch (err) {
      console.error("Erro ao remover nota:", err);
      toast.error("Erro ao remover nota");
    } finally {
      setLoading(false);
    }
  };

  const getTipoConfig = (tipo) => {
    const configs = {
      urgente: {
        color: "error",
        icon: <ErrorOutlineIcon />,
        label: "Urgente",
        bgColor: "#ffebee",
      },
      importante: {
        color: "warning",
        icon: <WarningIcon />,
        label: "Importante",
        bgColor: "#fff3e0",
      },
      normal: {
        color: "default",
        icon: <InfoIcon />,
        label: "Normal",
        bgColor: "#f5f5f5",
      },
    };
    return configs[tipo] || configs.normal;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Observações de Débitos - {nomeCliente}</DialogTitle>
      <DialogContent>
        {/* Seção para adicionar nova nota */}
        <Box sx={{ mb: 3, mt: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Nova Observação"
            value={novaNotaTexto}
            onChange={(e) => setNovaNotaTexto(e.target.value)}
            disabled={loading}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={novaNotaTipo}
                label="Tipo"
                onChange={(e) => setNovaNotaTipo(e.target.value)}
                disabled={loading}
              >
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="importante">Importante</MenuItem>
                <MenuItem value="urgente">Urgente</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={adicionarNota}
              disabled={loading || !novaNotaTexto.trim()}
            >
              Adicionar Nota
            </Button>
          </Box>
        </Box>

        {/* Lista de notas */}
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Histórico de Observações ({notasDebitos?.length})
          </Typography>

          {notasDebitos?.length === 0 ? (
            <Alert severity="info">
              Ainda não há observações registradas para este cliente.
            </Alert>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {[...notasDebitos]
                .sort((a, b) => new Date(b.data) - new Date(a.data))
                .map((nota) => {
                  const tipoConfig = getTipoConfig(nota.tipo);
                  const estaEditando = notaEditando === nota._id;

                  return (
                    <Card
                      key={nota._id}
                      sx={{
                        backgroundColor: tipoConfig.bgColor,
                        border:
                          nota.tipo === "urgente"
                            ? "2px solid #f44336"
                            : nota.tipo === "importante"
                            ? "2px solid #ff9800"
                            : "1px solid #e0e0e0",
                      }}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Chip
                            icon={tipoConfig.icon}
                            label={tipoConfig.label}
                            color={tipoConfig.color}
                            size="small"
                          />
                          <Box>
                            {!estaEditando && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() => iniciarEdicao(nota)}
                                  disabled={loading}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <Popconfirm
                                  title="Remover Nota"
                                  description="Tem certeza que deseja remover esta nota?"
                                  onConfirm={() => removerNota(nota._id)}
                                  okText="Sim"
                                  cancelText="Não"
                                  getPopupContainer={(trigger) =>
                                    trigger.parentElement
                                  }
                                  overlayStyle={{ zIndex: 1400 }}
                                >
                                  <IconButton size="small" disabled={loading}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Popconfirm>
                              </>
                            )}
                          </Box>
                        </Box>

                        {estaEditando ? (
                          <Box>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              value={textoEditando}
                              onChange={(e) => setTextoEditando(e.target.value)}
                              disabled={loading}
                              sx={{ mb: 1 }}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                              }}
                            >
                              <FormControl sx={{ minWidth: 150 }} size="small">
                                <InputLabel>Tipo</InputLabel>
                                <Select
                                  value={tipoEditando}
                                  label="Tipo"
                                  onChange={(e) =>
                                    setTipoEditando(e.target.value)
                                  }
                                  disabled={loading}
                                >
                                  <MenuItem value="normal">Normal</MenuItem>
                                  <MenuItem value="importante">
                                    Importante
                                  </MenuItem>
                                  <MenuItem value="urgente">Urgente</MenuItem>
                                </Select>
                              </FormControl>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={salvarEdicao}
                                disabled={loading}
                              >
                                Salvar
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={cancelarEdicao}
                                disabled={loading}
                              >
                                Cancelar
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <>
                            <Typography
                              variant="body1"
                              sx={{ mb: 2, whiteSpace: "pre-wrap" }}
                            >
                              {nota.texto}
                            </Typography>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Criado por {nota.utilizador?.name || "N/A"} em{" "}
                                {moment(nota.data).format("DD/MM/YYYY HH:mm")}
                              </Typography>
                              {nota.editadoEm && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  Editado por {nota.editadoPor?.name || "N/A"}{" "}
                                  em{" "}
                                  {moment(nota.editadoEm).format(
                                    "DD/MM/YYYY HH:mm"
                                  )}
                                </Typography>
                              )}
                            </Box>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalNotasDebitos;
